/**
 * usePushNotifications – React Hook (REQ-008)
 * Manages push subscription state and permission
 */
import { useCallback, useEffect, useState } from "react";
import {
  getPushPermissionState,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "../services/pushService";
import { useAuthStore } from "../stores/authStore";

interface PushState {
  isSupported: boolean;
  permission: NotificationPermission;
  subscriptionId: string | null;
  isLoading: boolean;
}

const STORAGE_KEY = "hp_push_subscription_id";

export function usePushNotifications() {
  const { token } = useAuthStore();
  const [state, setState] = useState<PushState>({
    isSupported: isPushSupported(),
    permission: "default",
    subscriptionId: localStorage.getItem(STORAGE_KEY),
    isLoading: false,
  });

  useEffect(() => {
    getPushPermissionState().then((permission) =>
      setState((s) => ({ ...s, permission })),
    );
  }, []);

  const subscribe = useCallback(async () => {
    if (!token) return;
    setState((s) => ({ ...s, isLoading: true }));

    const permission = await Notification.requestPermission();
    setState((s) => ({ ...s, permission }));

    if (permission !== "granted") {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    const id = await subscribeToPush(token);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
      setState((s) => ({ ...s, subscriptionId: id, isLoading: false }));
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, [token]);

  const unsubscribe = useCallback(async () => {
    if (!token || !state.subscriptionId) return;
    setState((s) => ({ ...s, isLoading: true }));
    await unsubscribeFromPush(token, state.subscriptionId);
    localStorage.removeItem(STORAGE_KEY);
    setState((s) => ({ ...s, subscriptionId: null, isLoading: false }));
  }, [token, state.subscriptionId]);

  const isSubscribed = state.permission === "granted" && !!state.subscriptionId;

  return { ...state, isSubscribed, subscribe, unsubscribe };
}
